import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// KTK-395 — CLAUDE.md composition editor.
//
// One component serves both surfaces, because they are the same object viewed
// at different depths: workspace settings edits layer 2, agent settings edits
// layer 3, and both want to see what the agent actually ends up with.
//
// Three product rules this file implements, decided by team-lead so the three
// lanes could build in parallel:
//
//  1. Never auto-resolve drift. If the file on the host diverged from what we
//     wrote, show the diff and let the user choose. Neither side is
//     authoritative — Akari asked to be alerted, not overruled.
//  2. Never imply an edit is live. A save reaches the running agent on its
//     next spawn, so the notice is scoped to "Kritaka's layer applies on
//     restart" — deliberately NOT "your prompt will match this preview".
//     CLAUDE.md discovery can also fire on a directory entered mid-session
//     (observed first-hand while building this), and whether the runtime's
//     env-var short-circuit covers that path is still open. Promising the
//     stronger thing is the KTK-377 shape: a UI claiming a change took
//     effect when it didn't.
//  3. The platform layer is not editable here. It carries the `author_type`
//     trust boundary; a layer below must not be able to edit away the rule
//     that says other agents can't authorize you. (The API enforces this too
//     — this is the half that stops someone being offered a control that
//     will 403.)
//
// A fourth thing, which is a correctness rule rather than a product one:
// `in_sync` means "the file we wrote matches the hash we wrote". It does NOT
// mean the agent's prompt equals the composed preview. Host CLAUDE.md files
// the runtime found are rendered from `inherited_neutralised`: struck through
// when discovery really was short-circuited, and as a live error when it
// wasn't — because then the agent is reading two instruction sets. A green
// tick over a hidden second instruction set is worse than no tick at all.
import { isClaudeMdLayerEditable, } from "@kritaka/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { PromptEditor } from "./PromptEditor.js";
/** The layer this scope is allowed to edit. */
function editableLayerFor(scope) {
    return scope.kind === "workspace" ? "workspace" : "agent";
}
export function ClaudeMdEditor({ scope, onRestartAgent }) {
    const hub = useDataContext().hub;
    const [doc, setDoc] = useState(null);
    const [draft, setDraft] = useState("");
    const [view, setView] = useState("edit");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [localFile, setLocalFile] = useState(null);
    const targetLayer = editableLayerFor(scope);
    const scopeKey = scope.kind === "workspace" ? scope.workspaceId : scope.agentId;
    const load = useCallback(async () => {
        setError(null);
        try {
            const next = scope.kind === "workspace"
                ? await hub.getWorkspaceClaudeMd(scope.workspaceId)
                : await hub.getAgentClaudeMd(scope.workspaceId, scope.agentId);
            setDoc(next);
            // Layers key on `scope`, and an unauthored layer is absent entirely —
            // so "not found" here means never written, which is exactly the empty
            // draft we want to start from.
            setDraft(next.layers.find((l) => l.scope === targetLayer)?.content ?? "");
        }
        catch (err) {
            // Surfaced, never swallowed into an empty editor: a failed load that
            // renders as a blank textarea invites someone to "fix" it by saving
            // emptiness over a layer that was fine (KTK-367's family).
            setError(err instanceof Error ? err.message : String(err));
        }
    }, [hub, scope, targetLayer]);
    useEffect(() => {
        void load();
        // scopeKey rather than the scope object: the parent rebuilds the object
        // each render, which would refetch (and discard the draft) on every keystroke.
    }, [scopeKey, load]);
    // Null until the agent's host has reported at least once — an agent whose
    // runtime has never checked in is a normal state, not an error.
    const sync = doc?.sync ?? null;
    // "Conflict" isn't its own flag: it *is* the drifted state. Deriving it here
    // rather than trusting a second field keeps the two from disagreeing.
    const conflict = sync?.state === "drifted";
    const savedContent = doc?.layers.find((l) => l.scope === targetLayer)?.content ?? "";
    const dirty = draft !== savedContent;
    const save = async () => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const next = scope.kind === "workspace"
                ? await hub.setWorkspaceClaudeMd(scope.workspaceId, draft)
                : await hub.setAgentClaudeMd(scope.workspaceId, scope.agentId, draft);
            setDoc(next);
            setNotice(next.sync?.state === "drifted"
                ? "Saved in Kritaka, but the file on the host has its own changes — resolve below."
                : "Saved.");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setBusy(false);
        }
    };
    const openDiff = () => {
        if (scope.kind !== "agent")
            return;
        // The host's copy rides along on the state the runtime reports, so opening
        // the diff needs no extra round-trip. Where we don't have it, say which
        // kind of "don't have it" this is rather than diffing against an empty
        // string — that renders as "the host deleted everything" and could talk
        // someone into overwriting a file they never actually saw.
        //
        // The two absences are different facts and get different sentences: no
        // report at all (`sync == null`) versus a report that carried no content.
        // Collapsing them would be the same defect this component exists to avoid.
        if (sync == null) {
            setError("This agent's host hasn't reported on its CLAUDE.md yet, so there's nothing " +
                "to compare against. It reports on its next check-in.");
            return;
        }
        if (typeof sync.observed_content !== "string") {
            setError("This agent's host reported that the file changed but didn't send its " +
                "contents, so there's nothing to diff yet. It's included on the host's " +
                "next check.");
            return;
        }
        setLocalFile(sync.observed_content);
        setView("diff");
    };
    const resolve = async (resolution) => {
        if (scope.kind !== "agent")
            return;
        setBusy(true);
        setError(null);
        try {
            const next = await hub.resolveAgentClaudeMd(scope.workspaceId, scope.agentId, resolution);
            setDoc(next);
            setDraft(next.layers.find((l) => l.scope === targetLayer)?.content ?? "");
            setLocalFile(null);
            setView("edit");
            setNotice(resolution === "keep_local"
                ? "Adopted the host's file into this agent's layer."
                : "Overwrote the host's file with Kritaka's version.");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setBusy(false);
        }
    };
    if (error && !doc) {
        return (_jsx(Section, { title: "CLAUDE.md", children: _jsxs(Banner, { tone: "error", children: ["Couldn\u2019t load CLAUDE.md: ", error, _jsx("button", { style: linkBtn, onClick: () => void load(), children: "Retry" })] }) }));
    }
    if (!doc) {
        return (_jsx(Section, { title: "CLAUDE.md", children: _jsx("p", { style: { color: "var(--text-muted)" }, children: "Loading\u2026" }) }));
    }
    return (_jsxs(Section, { title: "CLAUDE.md", subtitle: scope.kind === "workspace"
            ? "Baseline instructions every agent in this workspace inherits, on top of the Kritaka platform layer."
            : `Overrides for ${scope.agentName}, on top of the platform and workspace layers.`, children: [sync && (_jsx(StatusStrip, { sync: sync, conflict: conflict, onOpenDiff: openDiff })), !doc.content.trim() && (_jsxs(Banner, { tone: "error", children: [_jsx("strong", { children: "Composed CLAUDE.md is empty." }), " Agents using it would start with no onboarding at all \u2014 not with their previous instructions, because Kritaka replaces the filesystem lookup rather than adding to it. Fill this in before any agent is switched over."] })), sync?.inherited_paths?.length ? (_jsxs(Banner, { tone: sync.inherited_neutralised ? "info" : "error", children: [sync.inherited_neutralised ? (_jsxs(_Fragment, { children: [_jsxs("strong", { children: [sync.inherited_paths.length, " CLAUDE.md file", sync.inherited_paths.length === 1 ? "" : "s", " on the host", " ", sync.inherited_paths.length === 1 ? "is" : "are", " being ignored."] }), " ", "Kritaka resolves the hierarchy itself, so Claude Code\u2019s filesystem lookup is short-circuited and the composed text below is exactly what this agent reads. Listed so the files aren\u2019t a mystery \u2014 they are not in effect."] })) : (_jsxs(_Fragment, { children: [_jsxs("strong", { children: ["This agent is still reading ", sync.inherited_paths.length, " CLAUDE.md file", sync.inherited_paths.length === 1 ? "" : "s", " Kritaka doesn\u2019t control."] }), " ", "They stack on top of the composed text below, so its real instructions are not what you see here \u2014 and two instruction sets in one prompt is worse than either alone. This should not persist: Kritaka is meant to override the filesystem lookup, not merge with it."] })), _jsx("ul", { style: { margin: "6px 0 0", paddingLeft: 18 }, children: sync.inherited_paths.map((path) => (_jsx("li", { style: {
                                fontFamily: "var(--mono)",
                                fontSize: 12,
                                textDecoration: sync.inherited_neutralised ? "line-through" : "none",
                                opacity: sync.inherited_neutralised ? 0.7 : 1,
                            }, children: path }, path))) })] })) : null, error && _jsx(Banner, { tone: "error", children: error }), notice && _jsx(Banner, { tone: "info", children: notice }), _jsxs("div", { style: { display: "flex", gap: 4, margin: "10px 0" }, children: [_jsx(Tab, { active: view === "edit", onClick: () => setView("edit"), children: targetLayer === "workspace" ? "Workspace layer" : "Agent layer" }), _jsx(Tab, { active: view === "composed", onClick: () => setView("composed"), children: "Composed preview" }), conflict && (_jsx(Tab, { active: view === "diff", onClick: openDiff, children: "Resolve conflict" }))] }), view === "edit" && (_jsxs(_Fragment, { children: [_jsx(PromptEditor, { value: draft, onChange: setDraft, defaultLines: 24 }), _jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10, marginTop: 8 }, children: [_jsx("button", { style: primaryBtn, disabled: !dirty || busy, onClick: () => void save(), children: busy ? "Saving…" : "Save" }), dirty && (_jsx("span", { style: { color: "var(--text-muted)", fontSize: 12 }, children: "Unsaved changes" }))] })] })), view === "composed" && (_jsxs(_Fragment, { children: [_jsxs("p", { style: { color: "var(--text-muted)", fontSize: 12, margin: "0 0 6px" }, children: ["Platform \u2192 workspace", scope.kind === "agent" ? " → agent" : "", ", exactly as it will be written to disk."] }), _jsx(ReadOnlyBlock, { text: doc.content }), _jsx(LayerLegend, { layers: doc.layers })] })), view === "diff" && localFile !== null && (_jsx(ConflictResolver, { kritaka: doc.content, local: localFile, busy: busy, onResolve: (r) => void resolve(r), onCancel: () => setView("edit") })), sync?.applies_on_restart && (_jsxs(Banner, { tone: "info", children: [_jsx("strong", { children: "Kritaka\u2019s layer applies on restart." }), " The composed text is written to disk now, but", " ", scope.kind === "agent" ? scope.agentName : "the agent", " reads it when a session starts \u2014 so the running session still has the previous version.", onRestartAgent && (_jsx("button", { style: linkBtn, disabled: busy, onClick: () => void onRestartAgent(), children: "Restart now" }))] }))] }));
}
/* ---------- presentation ---------- */
function StatusStrip({ sync, conflict, onOpenDiff, }) {
    // `in_sync` is stated narrowly on purpose — see the header comment.
    const map = {
        in_sync: {
            tone: "ok",
            label: "In sync",
            detail: "The file on the host matches what Kritaka last wrote.",
        },
        drifted: {
            tone: "warn",
            label: "Edited on the host",
            detail: "The file changed outside Kritaka. Nothing has been overwritten — choose which version to keep.",
        },
        missing: {
            tone: "error",
            label: "Missing on host",
            detail: "Kritaka wrote a CLAUDE.md for this agent and it is no longer there.",
        },
        unmanaged: {
            tone: "warn",
            label: "Not managed",
            detail: "Kritaka has never written this agent's CLAUDE.md. Saving below will start managing it.",
        },
    };
    const s = map[sync.state];
    return (_jsxs(Banner, { tone: s.tone, children: [_jsxs("strong", { children: [s.label, "."] }), " ", s.detail, sync.observed_at && (_jsxs("span", { style: { color: "var(--text-muted)" }, children: [" ", "Checked ", new Date(sync.observed_at).toLocaleTimeString(), "."] })), conflict && (_jsx("button", { style: linkBtn, onClick: onOpenDiff, children: "View diff" }))] }));
}
function LayerLegend({ layers }) {
    if (layers.length === 0) {
        return (_jsx("p", { style: { margin: "8px 0 0", color: "var(--text-muted)", fontSize: 12 }, children: "No layers returned for this scope." }));
    }
    return (_jsx("ul", { style: { margin: "8px 0 0", paddingLeft: 18, color: "var(--text-muted)", fontSize: 12 }, children: layers.map((l) => (_jsxs("li", { children: [_jsx("strong", { style: { color: "var(--text-primary)" }, children: l.scope }), " ", "\u2014 ", l.content.trim() ? `${l.content.split("\n").length} lines` : "authored but empty", isClaudeMdLayerEditable(l.scope)
                    ? ""
                    : " · read-only (carries the authorization boundary)"] }, l.scope))) }));
}
/**
 * Line-level diff via longest-common-subsequence. Small input (a prompt file),
 * so the quadratic table is fine and exact beats approximate here — a diff
 * that mis-attributes a line is worse than none when someone is choosing which
 * copy of their instructions to destroy.
 */
function diffLines(a, b) {
    const n = a.length;
    const m = b.length;
    const lcs = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }
    const out = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            out.push({ tag: " ", text: a[i] });
            i++;
            j++;
        }
        else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
            out.push({ tag: "-", text: a[i] });
            i++;
        }
        else {
            out.push({ tag: "+", text: b[j] });
            j++;
        }
    }
    while (i < n)
        out.push({ tag: "-", text: a[i++] });
    while (j < m)
        out.push({ tag: "+", text: b[j++] });
    return out;
}
function ConflictResolver({ kritaka, local, busy, onResolve, onCancel, }) {
    const rows = useMemo(() => diffLines(kritaka.split("\n"), local.split("\n")), [kritaka, local]);
    return (_jsxs("div", { children: [_jsxs("p", { style: { color: "var(--text-muted)", fontSize: 12, margin: "0 0 6px" }, children: [_jsx("span", { style: { color: "var(--error)" }, children: "\u2212" }), " Kritaka\u2019s version \u00B7", " ", _jsx("span", { style: { color: "var(--success)" }, children: "+" }), " the file on the host. Nothing is changed until you choose."] }), _jsx("div", { style: {
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--paper-2)",
                    maxHeight: 420,
                    overflow: "auto",
                    fontFamily: "var(--mono)",
                    fontSize: 12,
                    lineHeight: 1.5,
                }, children: rows.map((r, idx) => (_jsxs("div", { style: {
                        display: "flex",
                        gap: 8,
                        padding: "0 8px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        background: r.tag === "-"
                            ? "color-mix(in srgb, var(--error) 12%, transparent)"
                            : r.tag === "+"
                                ? "color-mix(in srgb, var(--success) 12%, transparent)"
                                : "transparent",
                        color: r.tag === " " ? "var(--text-muted)" : "var(--text-primary)",
                    }, children: [_jsx("span", { style: { userSelect: "none", opacity: 0.7 }, children: r.tag }), _jsx("span", { style: { flex: 1 }, children: r.text || " " })] }, idx))) }), _jsxs("div", { style: { display: "flex", gap: 8, marginTop: 10 }, children: [_jsx("button", { style: primaryBtn, disabled: busy, onClick: () => onResolve("keep_local"), children: "Keep the host\u2019s version" }), _jsx("button", { style: secondaryBtn, disabled: busy, onClick: () => onResolve("take_kritaka"), children: "Take Kritaka\u2019s version" }), _jsx("button", { style: linkBtn, disabled: busy, onClick: onCancel, children: "Decide later" })] })] }));
}
function ReadOnlyBlock({ text }) {
    return (_jsx("pre", { style: {
            margin: 0,
            padding: 10,
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper-2)",
            color: "var(--text-primary)",
            fontFamily: "var(--mono)",
            fontSize: 12,
            lineHeight: 1.5,
            maxHeight: 420,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        }, children: text }));
}
function Banner({ tone, children }) {
    const accent = tone === "ok"
        ? "var(--success)"
        : tone === "error"
            ? "var(--error)"
            : tone === "warn"
                ? "var(--accent)"
                : "var(--text-muted)";
    return (_jsx("div", { style: {
            margin: "8px 0",
            padding: "8px 10px",
            borderLeft: `3px solid ${accent}`,
            borderRadius: "var(--radius-sm)",
            // Tokenised rather than a fixed hex — a hardcoded surface here is
            // exactly what stranded text in light theme (KTK-384).
            background: "var(--paper-2)",
            color: "var(--text-primary)",
            fontSize: 13,
        }, children: children }));
}
function Section({ title, subtitle, children, }) {
    return (_jsxs("div", { style: { padding: "4px 0 20px" }, children: [_jsx("h3", { style: { margin: "0 0 2px", color: "var(--text-primary)" }, children: title }), subtitle && (_jsx("p", { style: { margin: "0 0 8px", color: "var(--text-muted)", fontSize: 13 }, children: subtitle })), children] }));
}
function Tab({ active, onClick, children, }) {
    return (_jsx("button", { onClick: onClick, style: {
            padding: "4px 10px",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            background: active ? "var(--accent-soft)" : "transparent",
            color: active ? "var(--accent-ink)" : "var(--text-muted)",
            cursor: "pointer",
            fontSize: 12,
        }, children: children }));
}
const primaryBtn = {
    padding: "5px 12px",
    border: "1px solid var(--accent)",
    borderRadius: "var(--radius-sm)",
    background: "var(--accent)",
    color: "var(--accent-ink)",
    cursor: "pointer",
    fontSize: 13,
};
const secondaryBtn = {
    padding: "5px 12px",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    background: "transparent",
    color: "var(--text-primary)",
    cursor: "pointer",
    fontSize: 13,
};
const linkBtn = {
    marginLeft: 8,
    padding: 0,
    border: "none",
    background: "none",
    color: "var(--accent)",
    cursor: "pointer",
    fontSize: 13,
    textDecoration: "underline",
};
//# sourceMappingURL=ClaudeMdEditor.js.map